import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Modal from '../Modal';
import useToast from '../../../hooks/useToast';
import { api } from '../../../services/api';
import { applyAnonymizationToPdf } from '../../../utils/pdfAnonymizeUtils';

export interface ModalAnonymizeLgpdProps {
  documentId?: string;
  documentName?: string;
  documentBlob?: ArrayBuffer | null;
  onSuccess?: () => void;
}

export interface ModalAnonymizeLgpdHandle {
  openModal: (docId?: string, blob?: ArrayBuffer) => void;
  closeModal: () => void;
}

const ModalAnonymizeLgpd: React.ForwardRefRenderFunction<ModalAnonymizeLgpdHandle, ModalAnonymizeLgpdProps> = (
  { documentId, documentName, documentBlob, onSuccess },
  ref
) => {
  const modalRef = React.useRef<any>(null);
  const toast = useToast();

  const [activeDocId, setActiveDocId] = useState<string | undefined>(documentId);
  const [activeBlob, setActiveBlob] = useState<ArrayBuffer | null | undefined>(documentBlob);

  const [watermarkType, setWatermarkType] = useState<string>('CONFIDENCIAL - LEI GERAL DE PROTEÇÃO DE DADOS');
  const [customWatermark, setCustomWatermark] = useState<string>('');
  const [applyWatermark, setApplyWatermark] = useState(true);
  const [applyBlackoutHeader, setApplyBlackoutHeader] = useState(true);
  const [applyBlackoutFooter, setApplyBlackoutFooter] = useState(false);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(docId, blob) {
      if (docId) setActiveDocId(docId);
      if (blob) setActiveBlob(blob);
      modalRef.current?.openModal();
    },
    closeModal() {
      modalRef.current?.closeModal();
    },
  }));

  const handleApply = async () => {
    const docIdToProcess = activeDocId || documentId;
    const blobToProcess = activeBlob || documentBlob;

    if (!docIdToProcess) {
      toast.errorToast('Identificador do documento não fornecido');
      return;
    }

    if (!blobToProcess) {
      toast.errorToast('Arquivo PDF ainda não foi totalmente carregado');
      return;
    }

    try {
      setLoading(true);

      const watermarkText = applyWatermark
        ? watermarkType === 'CUSTOM'
          ? customWatermark
          : watermarkType
        : undefined;

      const modifiedBytes = await applyAnonymizationToPdf(blobToProcess, {
        watermarkText,
        applyBlackoutHeader,
        applyBlackoutFooter,
      });

      const blob = new Blob([modifiedBytes], { type: 'application/pdf' });
      const anonymizedFile = new File([blob], documentName || 'documento_anonimizado_lgpd.pdf', {
        type: 'application/pdf',
      });

      const formData = new FormData();
      formData.append('document', anonymizedFile);
      formData.append('action', 'Anonimização e Proteção LGPD');

      await api.post(`/documents/${docIdToProcess}/save-version`, formData);

      toast.successToast('Documento anonimizado e nova versão salva com sucesso!');
      modalRef.current?.closeModal();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erro ao aplicar anonimização LGPD:', err);
      toast.errorToast(err?.message || 'Erro ao processar anonimização');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Anonimização & Proteção de Dados (LGPD)" ref={modalRef} maxWidth={520}>
      <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Banner Informativo LGPD */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            padding: '16px',
            borderRadius: '8px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '20px' }}>🛡️</span>
              <span style={{ fontSize: '15px', fontWeight: 800 }}>Conformidade com a Lei nº 13.709/2018</span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#cbd5e1' }}>
              Tarjamento permanente e chancela de confidencialidade antes de expedição externa ou consulta pública.
            </p>
          </div>
        </div>

        {/* Opções de Tarjamento */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: 8 }}>
              1. Tarjamento Físico de Dados Sensíveis (Blackout)
            </span>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#334155', cursor: 'pointer', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={applyBlackoutHeader}
                onChange={(e) => setApplyBlackoutHeader(e.target.checked)}
              />
              <span>Tarjar Dados de Identificação no Cabeçalho (ex: CPF, RG, filiação)</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={applyBlackoutFooter}
                onChange={(e) => setApplyBlackoutFooter(e.target.checked)}
              />
              <span>Tarjar Dados Financeiros e Assinaturas no Rodapé (ex: conta bancária)</span>
            </label>
          </div>

          <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: 8 }}>
              2. Marca d'Água de Segurança e Restrição de Acesso
            </span>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#334155', cursor: 'pointer', marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={applyWatermark}
                onChange={(e) => setApplyWatermark(e.target.checked)}
              />
              <span>Inserir Marca d'Água Diagonal nas Páginas</span>
            </label>

            {applyWatermark && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                <select
                  value={watermarkType}
                  onChange={(e) => setWatermarkType(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                    background: '#fff',
                  }}
                >
                  <option value="CONFIDENCIAL - LEI GERAL DE PROTEÇÃO DE DADOS">
                    CONFIDENCIAL - LEI GERAL DE PROTEÇÃO DE DADOS
                  </option>
                  <option value="DOCUMENTO CONTROLADO - USO INTERNO">
                    DOCUMENTO CONTROLADO - USO INTERNO
                  </option>
                  <option value="CÓPIA NÃO AUTORIZADA">
                    CÓPIA NÃO AUTORIZADA
                  </option>
                  <option value="CUSTOM">
                    Outro texto personalizado...
                  </option>
                </select>

                {watermarkType === 'CUSTOM' && (
                  <input
                    type="text"
                    value={customWatermark}
                    onChange={(e) => setCustomWatermark(e.target.value)}
                    placeholder="Digite o texto da marca d'água"
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => modalRef.current?.closeModal()}
            style={{
              padding: '10px 16px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              background: '#f1f5f9',
              color: '#475569',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              border: 'none',
              background: '#0f172a',
              color: 'white',
              fontWeight: 700,
              fontSize: '13px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {loading ? 'Processando Anonimização...' : '🛡️ Aplicar Proteção LGPD'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(ModalAnonymizeLgpd);
